import {useMemo} from 'react'
import type {Iri} from '../@types/Api'
import type {Jwt} from '../@types/Jwt'
import type {MembershipPartial} from '../@types/entity/Membership'
import {useLogged} from '../states/auth'
import {useCurrentSpotIri} from '../states/currentSpot'
import {createCollectionIri} from '../utils/iri'
import {useMembershipCollection, useMembershipFilterFromCurrentUser, useMembershipGet} from './entity/useMembership'
import {useOrganizationGet, useOrganizationIriFromCurrentSpot} from './entity/useOrganization'
import {useSpotCollection, useSpotGet} from './entity/useSpot'
import {useUserHelperGetCurrent} from './entity/useUser'
import {useEffectEvent} from './useEffectEvent'
import {useFetcher} from './useFetcher'

type Action = 'admin_settings' | 'admin_route' | 'admin_comment' | 'admin'

export function useSignin(): (email: string) => Promise<void> {
  const {fetcher} = useFetcher()

  return function spotList(email: string): Promise<void> {
    const url = createCollectionIri('auth/signin')

    return fetcher(url, {
      method: 'POST',
      body: JSON.stringify({
        email,
        targetPath: window.location.pathname,
      }),
    })
  }
}

export function useTotpValidate(): (payload: object) => Promise<Jwt> {
  const {fetcher} = useFetcher()

  return useEffectEvent((payload: object): Promise<Jwt> => {
    return fetcher(createCollectionIri('auth/totp'), {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  })
}

function usePartialMembership(spotIri: Iri | undefined): MembershipPartial | undefined | null {
  const {user} = useUserHelperGetCurrent()
  const {memberships} = useMembershipCollection({spotIri})

  return useMemo(() => {
    if (!memberships || !user) {
      return undefined
    }
    for (const membership of memberships.member) {
      if (membership.user === user['@id']) {
        return membership
      }
    }

    return null
  }, [memberships, user])
}

export function useIsOrganizationAdmin(spotIri?: Iri): boolean | undefined {
  const currentSpotIri = useCurrentSpotIri()
  const {spot} = useSpotGet(spotIri ?? currentSpotIri)
  const logged = useLogged()
  const {organization} = useOrganizationGet(useOrganizationIriFromCurrentSpot())
  const partialMembership = usePartialMembership(spotIri ?? currentSpotIri)
  const isDemo = spot?.demo
  const {spots: organizationSpots} = useSpotCollection({organizationIri: spot?.organization})
  const organizationSpotIris = useMemo(() => {
    if (!organizationSpots) {
      return undefined
    }
    return organizationSpots.member.map((spot) => spot['@id'])
  }, [organizationSpots])
  const {memberships: organizationMemberships} = useMembershipCollection({
    ...useMembershipFilterFromCurrentUser(),
    spotIris: organizationSpotIris ?? [],
  })

  return useMemo(() => {
    if (isDemo) {
      return true
    }

    if (logged === false) {
      return false
    }

    if (!organizationMemberships || !organizationSpotIris) {
      if (partialMembership === undefined) {
        return undefined
      }
      if (partialMembership === null || partialMembership.role !== 'admin') {
        return false
      }
      if (organization && organization.quota.spot <= 1) {
        return true
      }

      return undefined
    }

    const adminMembership = organizationMemberships.member
      .filter((membership) => membership.role === 'admin')

    return adminMembership.length === organizationSpotIris.length
  }, [isDemo, logged, organization, organizationMemberships, organizationSpotIris, partialMembership])
}

export function useIsAllowed(spotIri?: Iri): (action: Action) => boolean | undefined {
  const currentSpotIri = useCurrentSpotIri()
  const {spot} = useSpotGet(spotIri ?? currentSpotIri)
  const logged = useLogged()
  const isDemo = spot?.demo

  const partialMembership = usePartialMembership(spotIri ?? currentSpotIri)
  const {membership} = useMembershipGet(partialMembership?.['@id'])

  return useEffectEvent((action: Action): boolean | undefined => {
    if (isDemo) {
      return true
    }

    if (logged === false) {
      return false
    }

    if (partialMembership === undefined) {
      return undefined
    }

    if (!partialMembership) {
      return false
    }

    if (action === 'admin') {
      return true
    }

    if (!membership) {
      return undefined
    }

    return membership.permissions[action] ?? false
  })
}
